// src/pages/trading-portal/MarketDataIngestion/BitcoinCorrelationPage.js
// Bitcoin cross-asset correlation dashboard

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Alert,
    Button,
    TextField,
    Table,
    TableContainer,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TablePagination,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Card,
    CardContent,
    Chip,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Tabs,
    Tab,
    Tooltip,
    Autocomplete,
} from '@mui/material';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CalculateIcon from '@mui/icons-material/Calculate';
import WarningIcon from '@mui/icons-material/Warning';
import AssessmentIcon from '@mui/icons-material/Assessment';
import RefreshIcon from '@mui/icons-material/Refresh';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';
import {
    CorrelationAssetType,
    CorrelationAssetTypeLabels,
    MacroRegime,
    MacroRegimeLabels,
    MacroRegimeColors,
} from '../stockTradingEnums';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#f59e0b';

const BitcoinCorrelationPage = () => {
    // Permission checks
    const [canView, setCanView] = useState(false);
    const [canAdd, setCanAdd] = useState(false);

    // Tab state
    const [activeTab, setActiveTab] = useState(0);

    // Dashboard state (Tab 0)
    const [dashboardData, setDashboardData] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true);
    const [dashboardError, setDashboardError] = useState(null);
    const [fetchingPrice, setFetchingPrice] = useState(false);
    const [fetchPriceResult, setFetchPriceResult] = useState(null);
    const [fetchPriceDays, setFetchPriceDays] = useState(30);
    const [correlationAlerts, setCorrelationAlerts] = useState([]);
    const [alertsLoading, setAlertsLoading] = useState(false);

    // Calculate Correlation state (Tab 1)
    const [symbols, setSymbols] = useState([]);
    const [calcForm, setCalcForm] = useState({
        asset_a_symbol: 'BTC',
        asset_b_symbol: null,
        window_days: 30,
        lag_days: 0,
    });
    const [calculating, setCalculating] = useState(false);
    const [calcResult, setCalcResult] = useState(null);
    const [calcError, setCalcError] = useState(null);

    // Correlations list state (Tab 1)
    const [correlations, setCorrelations] = useState([]);
    const [corrLoading, setCorrLoading] = useState(false);
    const [corrPage, setCorrPage] = useState(0);
    const [corrRowsPerPage, setCorrRowsPerPage] = useState(10);
    const [corrTotalCount, setCorrTotalCount] = useState(0);
    const [corrAssetFilter, setCorrAssetFilter] = useState('');

    // Price History state (Tab 2)
    const [priceData, setPriceData] = useState([]);
    const [priceLoading, setPriceLoading] = useState(false);
    const [pricePage, setPricePage] = useState(0);
    const [priceRowsPerPage, setPriceRowsPerPage] = useState(25);
    const [priceTotalCount, setPriceTotalCount] = useState(0);
    const [priceStartDate, setPriceStartDate] = useState('');
    const [priceEndDate, setPriceEndDate] = useState('');

    // General error
    const [error, setError] = useState(null);

    useEffect(() => {
        const checkPermissions = async () => {
            const view = await hasPermission('tr-mdi-btc-correlation', 'view');
            const add = await hasPermission('tr-mdi-btc-correlation', 'add');
            setCanView(view);
            setCanAdd(add);
        };
        checkPermissions();
    }, []);

    // Fetch symbols for autocomplete
    const fetchSymbols = useCallback(async () => {
        try {
            const response = await axiosInstance.get('/api/stock-trading/symbols?per_page=200');
            if (response.data.success) {
                setSymbols(response.data.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch symbols:', err);
        }
    }, []);

    // ==================== TAB 0: Dashboard ====================
    const fetchDashboard = useCallback(async () => {
        if (!canView) return;
        setDashboardLoading(true);
        setDashboardError(null);
        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/btc/dashboard');
            const data = response.data?.data || response.data || {};
            setDashboardData(data);
        } catch (err) {
            console.error('Failed to fetch BTC dashboard:', err);
            setDashboardError(err.response?.data?.message || 'Failed to load dashboard data.');
        } finally {
            setDashboardLoading(false);
        }
    }, [canView]);

    const fetchCorrelationAlerts = useCallback(async () => {
        if (!canView) return;
        setAlertsLoading(true);
        try {
            const response = await axiosInstance.get('/api/market-data-ingestion/btc/correlation-alerts');
            setCorrelationAlerts(response.data?.data || response.data?.alerts || []);
        } catch (err) {
            console.error('Failed to fetch correlation alerts:', err);
        } finally {
            setAlertsLoading(false);
        }
    }, [canView]);

    const handleFetchPrice = async () => {
        setFetchingPrice(true);
        setFetchPriceResult(null);
        try {
            const res = await axiosInstance.post('/api/market-data-ingestion/btc/fetch-price', { days: fetchPriceDays });
            if (res.data.success) {
                setFetchPriceResult({ message: res.data.message || 'BTC price fetched successfully', severity: 'success' });
                fetchDashboard();
            } else {
                setFetchPriceResult({ message: res.data.message || 'Failed to fetch BTC price', severity: 'error' });
            }
        } catch (err) {
            const errMsg = err.response?.data?.message || 'Failed to fetch BTC price.';
            setFetchPriceResult({ message: errMsg, severity: 'error' });
        } finally {
            setFetchingPrice(false);
        }
    };

    // ==================== TAB 1: Calculate Correlation ====================
    const handleCalculateCorrelation = async () => {
        if (!calcForm.asset_b_symbol) {
            setCalcError('Please select Asset B symbol.');
            return;
        }
        setCalculating(true);
        setCalcResult(null);
        setCalcError(null);
        try {
            const payload = {
                asset_a_symbol: calcForm.asset_a_symbol,
                asset_b_symbol: typeof calcForm.asset_b_symbol === 'object'
                    ? calcForm.asset_b_symbol.symbol
                    : calcForm.asset_b_symbol,
                window_days: calcForm.window_days,
                lag_days: parseInt(calcForm.lag_days, 10) || 0,
            };
            const response = await axiosInstance.post('/api/market-data-ingestion/btc/calculate-correlation', payload);
            setCalcResult(response.data?.data || response.data || {});
            fetchCorrelations();
        } catch (err) {
            setCalcError(err.response?.data?.message || 'Correlation calculation failed.');
        } finally {
            setCalculating(false);
        }
    };

    const fetchCorrelations = useCallback(async () => {
        if (!canView) return;
        setCorrLoading(true);
        try {
            const params = new URLSearchParams({ page: corrPage + 1, per_page: corrRowsPerPage });
            if (corrAssetFilter) params.append('asset_symbol', corrAssetFilter);

            const response = await axiosInstance.get(`/api/market-data-ingestion/btc/correlations?${params.toString()}`);
            if (response.data.success !== false) {
                setCorrelations(response.data.data || []);
                setCorrTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            console.error('Failed to fetch correlations:', err);
        } finally {
            setCorrLoading(false);
        }
    }, [canView, corrPage, corrRowsPerPage, corrAssetFilter]);

    // ==================== TAB 2: Price History ====================
    const fetchPriceData = useCallback(async () => {
        if (!canView) return;
        setPriceLoading(true);
        try {
            const params = new URLSearchParams({ page: pricePage + 1, per_page: priceRowsPerPage });
            if (priceStartDate) params.append('start_date', priceStartDate);
            if (priceEndDate) params.append('end_date', priceEndDate);

            const response = await axiosInstance.get(`/api/market-data-ingestion/btc/price-data?${params.toString()}`);
            if (response.data.success !== false) {
                setPriceData(response.data.data || []);
                setPriceTotalCount(response.data.pagination?.total || 0);
            }
        } catch (err) {
            console.error('Failed to fetch BTC price data:', err);
        } finally {
            setPriceLoading(false);
        }
    }, [canView, pricePage, priceRowsPerPage, priceStartDate, priceEndDate]);

    // ==================== Effects ====================
    useEffect(() => {
        if (canView) {
            fetchSymbols();
            fetchDashboard();
            fetchCorrelationAlerts();
        }
    }, [canView, fetchSymbols, fetchDashboard, fetchCorrelationAlerts]);

    useEffect(() => {
        if (canView && activeTab === 1) {
            fetchCorrelations();
        }
    }, [canView, activeTab, fetchCorrelations]);

    useEffect(() => {
        if (canView && activeTab === 2) {
            fetchPriceData();
        }
    }, [canView, activeTab, fetchPriceData]);

    // ==================== Helpers ====================
    const formatBtcPrice = (price) => {
        if (price == null) return '--';
        return `$${parseFloat(price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatCorrelation = (val) => {
        if (val == null) return '--';
        return parseFloat(val).toFixed(4);
    };

    const getCorrelationColor = (val) => {
        if (val == null) return 'inherit';
        const num = parseFloat(val);
        if (num > 0.7) return '#10b981';
        if (num < -0.7) return '#ef4444';
        return 'inherit';
    };

    const formatLargeNumber = (num) => {
        if (num == null) return '--';
        return parseFloat(num).toLocaleString();
    };

    const handleTabChange = (event, newValue) => setActiveTab(newValue);

    // ==================== Permission Guard ====================
    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view Bitcoin Correlation data.</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Gradient Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'absolute', bottom: -40, right: 80, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 2 }}>
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <CurrencyBitcoinIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                    Bitcoin & Cross-Asset Correlation
                                </Typography>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    Track BTC as a leading indicator for risk appetite and liquidity conditions
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                    {/* BTC Quick Price */}
                    {dashboardData && (
                        <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: '14px', px: 2.5, py: 1.5, textAlign: 'center', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, justifyContent: 'center' }}>
                                <CurrencyBitcoinIcon sx={{ fontSize: 18, color: '#fff' }} />
                                <Typography sx={{ fontFamily, fontWeight: 800, color: '#fff', fontSize: '1.1rem', lineHeight: 1 }}>
                                    {dashboardLoading ? '--' : formatBtcPrice(dashboardData.latest_btc?.price)}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.7rem', fontWeight: 500, mt: 0.25 }}>
                                BTC Price
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {/* Error Alert */}
                {error && (
                    <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {/* Tabs */}
                <Paper sx={{ mb: 3, borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '& .MuiTab-root': { fontFamily, fontWeight: 600, textTransform: 'none', fontSize: '0.9rem' },
                            '& .Mui-selected': { color: ACCENT },
                            '& .MuiTabs-indicator': { backgroundColor: ACCENT },
                        }}
                    >
                        <Tab label="BTC Dashboard" />
                        <Tab label="Calculate Correlation" />
                        <Tab label="BTC Price History" />
                    </Tabs>
                </Paper>

                {/* ==================== TAB 0: BTC Dashboard ==================== */}
                {activeTab === 0 && (
                    <Box>
                        {dashboardError && (
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', fontFamily }} action={
                                <Button color="inherit" size="small" onClick={fetchDashboard} sx={{ fontFamily, textTransform: 'none', fontWeight: 600 }}>
                                    Retry
                                </Button>
                            }>
                                {dashboardError}
                            </Alert>
                        )}

                        {/* BTC Price Card */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                        <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: `${ACCENT}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <CurrencyBitcoinIcon sx={{ color: ACCENT, fontSize: 32 }} />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.8rem' }}>
                                                Bitcoin Price
                                            </Typography>
                                            <Typography variant="h3" sx={{ fontFamily, fontWeight: 800, color: '#1e293b', lineHeight: 1.2 }}>
                                                {dashboardLoading ? (
                                                    <CircularProgress size={32} />
                                                ) : (
                                                    formatBtcPrice(dashboardData?.latest_btc?.price)
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    {canAdd && (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <TextField
                                                size="small"
                                                type="number"
                                                label="Days"
                                                value={fetchPriceDays}
                                                onChange={(e) => setFetchPriceDays(Math.max(1, parseInt(e.target.value) || 1))}
                                                inputProps={{ min: 1, max: 365 }}
                                                sx={{ width: 80, fontFamily }}
                                            />
                                            <Button
                                                variant="contained"
                                                size="small"
                                                startIcon={fetchingPrice ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                                                onClick={handleFetchPrice}
                                                disabled={fetchingPrice}
                                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                                            >
                                                Fetch Price
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                                {fetchPriceResult && (
                                    <Alert
                                        severity={fetchPriceResult.severity}
                                        onClose={() => setFetchPriceResult(null)}
                                        sx={{ mb: 2, fontFamily }}
                                    >
                                        {fetchPriceResult.message}
                                    </Alert>
                                )}

                                {!dashboardLoading && dashboardData && (
                                    <Grid container spacing={2}>
                                        {/* 24h Change */}
                                        <Grid item xs={6} sm={3}>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#f8fafc' }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.75rem', mb: 0.5 }}>
                                                    24h Change
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    {parseFloat(dashboardData.btc_24h_change || 0) >= 0 ? (
                                                        <ArrowUpwardIcon sx={{ fontSize: 18, color: '#10b981' }} />
                                                    ) : (
                                                        <ArrowDownwardIcon sx={{ fontSize: 18, color: '#ef4444' }} />
                                                    )}
                                                    <Typography sx={{
                                                        fontFamily,
                                                        fontWeight: 700,
                                                        fontSize: '1rem',
                                                        color: parseFloat(dashboardData.btc_24h_change || 0) >= 0 ? '#10b981' : '#ef4444',
                                                    }}>
                                                        {dashboardData.btc_24h_change != null
                                                            ? `${parseFloat(dashboardData.btc_24h_change).toFixed(2)}%`
                                                            : '--'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Grid>
                                        {/* Volume */}
                                        <Grid item xs={6} sm={3}>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#f8fafc' }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.75rem', mb: 0.5 }}>
                                                    Volume
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                                    {formatLargeNumber(dashboardData.latest_btc?.volume)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {/* Market Cap */}
                                        <Grid item xs={6} sm={3}>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#f8fafc' }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.75rem', mb: 0.5 }}>
                                                    Market Cap
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                                    {formatLargeNumber(dashboardData.latest_btc?.market_cap)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        {/* Realized Volatility */}
                                        <Grid item xs={6} sm={3}>
                                            <Box sx={{ p: 1.5, borderRadius: '12px', bgcolor: '#f8fafc' }}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontWeight: 500, fontSize: '0.75rem', mb: 0.5 }}>
                                                    Realized Volatility
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                                    {dashboardData.latest_btc?.realized_volatility != null
                                                        ? `${parseFloat(dashboardData.latest_btc.realized_volatility).toFixed(4)}`
                                                        : '--'}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>

                        {/* Latest Correlations Table */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AssessmentIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Latest Correlations
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Refresh">
                                        <IconButton size="small" onClick={fetchDashboard}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Asset A</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Asset B</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Correlation</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Window</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Lag Days</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Leading Indicator</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Regime</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {dashboardLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                        <CircularProgress size={28} />
                                                    </TableCell>
                                                </TableRow>
                                            ) : (!dashboardData?.latest_correlations || dashboardData.latest_correlations.length === 0) ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                        <AssessmentIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                            No correlation data available
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                dashboardData.latest_correlations.map((corr, index) => (
                                                    <TableRow key={corr.id || index} hover>
                                                        <TableCell>
                                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                                {corr.asset_a_symbol || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                                {corr.asset_b_symbol || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{
                                                                fontFamily,
                                                                fontWeight: 700,
                                                                fontSize: '0.85rem',
                                                                color: getCorrelationColor(corr.correlation_coefficient),
                                                            }}>
                                                                {formatCorrelation(corr.correlation_coefficient)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {corr.window_days || '-'}d
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {corr.lag_days != null ? corr.lag_days : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={corr.is_leading_indicator ? 'Yes' : 'No'}
                                                                size="small"
                                                                color={corr.is_leading_indicator ? 'success' : 'default'}
                                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {corr.macro_regime ? (
                                                                <Chip
                                                                    label={MacroRegimeLabels[corr.macro_regime] || corr.macro_regime}
                                                                    size="small"
                                                                    color={MacroRegimeColors[corr.macro_regime] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                                {corr.calculated_at
                                                                    ? new Date(corr.calculated_at).toLocaleDateString()
                                                                    : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>

                        {/* Correlation Alerts */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <WarningIcon sx={{ color: '#ef4444', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Correlation Alerts
                                        </Typography>
                                    </Box>
                                    <Tooltip title="Refresh Alerts">
                                        <IconButton size="small" onClick={fetchCorrelationAlerts}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>

                                <Box sx={{ px: 2.5, pb: 2.5 }}>
                                    {alertsLoading ? (
                                        <Box sx={{ py: 3, textAlign: 'center' }}>
                                            <CircularProgress size={28} />
                                        </Box>
                                    ) : correlationAlerts.length === 0 ? (
                                        <Box sx={{ py: 4, textAlign: 'center' }}>
                                            <WarningIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                            <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem', fontWeight: 500 }}>
                                                No significant correlation shifts detected
                                            </Typography>
                                            <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                Alerts appear when correlations change significantly
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {correlationAlerts.map((alert, index) => (
                                                <Grid item xs={12} sm={6} md={4} key={alert.id || index}>
                                                    <Card sx={{
                                                        borderRadius: '12px',
                                                        border: '1px solid #fef3c7',
                                                        boxShadow: 'none',
                                                        bgcolor: '#fffbeb',
                                                    }}>
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                                                <WarningIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                                                                <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.85rem' }}>
                                                                    {alert.pair_name || `${alert.asset_a_symbol || '?'} / ${alert.asset_b_symbol || '?'}`}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem' }}>
                                                                    Previous
                                                                </Typography>
                                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: getCorrelationColor(alert.previous_correlation) }}>
                                                                    {formatCorrelation(alert.previous_correlation)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem' }}>
                                                                    Current
                                                                </Typography>
                                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: getCorrelationColor(alert.current_correlation) }}>
                                                                    {formatCorrelation(alert.current_correlation)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem' }}>
                                                                    Change
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    {parseFloat(alert.change_magnitude || 0) > 0 ? (
                                                                        <TrendingUpIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                                                    ) : (
                                                                        <TrendingDownIcon sx={{ fontSize: 14, color: '#ef4444' }} />
                                                                    )}
                                                                    <Typography sx={{
                                                                        fontFamily,
                                                                        fontWeight: 700,
                                                                        fontSize: '0.8rem',
                                                                        color: parseFloat(alert.change_magnitude || 0) > 0 ? '#10b981' : '#ef4444',
                                                                    }}>
                                                                        {alert.change_magnitude != null
                                                                            ? parseFloat(alert.change_magnitude).toFixed(4)
                                                                            : '--'}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* ==================== TAB 1: Calculate Correlation ==================== */}
                {activeTab === 1 && (
                    <Box>
                        {/* Calculation Form */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', mb: 3 }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                                    <CalculateIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                        Calculate Correlation
                                    </Typography>
                                </Box>

                                {calcError && (
                                    <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontFamily }} onClose={() => setCalcError(null)}>
                                        {calcError}
                                    </Alert>
                                )}

                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={6} md={3}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Asset A Symbol</InputLabel>
                                            <Select
                                                value={calcForm.asset_a_symbol}
                                                label="Asset A Symbol"
                                                onChange={(e) => setCalcForm(prev => ({ ...prev, asset_a_symbol: e.target.value }))}
                                                sx={{ fontFamily }}
                                            >
                                                <MenuItem value="BTC">BTC - Bitcoin</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Autocomplete
                                            options={symbols}
                                            getOptionLabel={(option) => typeof option === 'string' ? option : `${option.symbol} - ${option.company_name || ''}`}
                                            value={calcForm.asset_b_symbol}
                                            onChange={(e, newValue) => setCalcForm(prev => ({ ...prev, asset_b_symbol: newValue }))}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Asset B Symbol"
                                                    size="small"
                                                    sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel sx={{ fontFamily }}>Window Days</InputLabel>
                                            <Select
                                                value={calcForm.window_days}
                                                label="Window Days"
                                                onChange={(e) => setCalcForm(prev => ({ ...prev, window_days: e.target.value }))}
                                                sx={{ fontFamily }}
                                            >
                                                <MenuItem value={30}>30 Days</MenuItem>
                                                <MenuItem value={60}>60 Days</MenuItem>
                                                <MenuItem value={90}>90 Days</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={6} sm={4} md={2}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Lag Days"
                                            type="number"
                                            value={calcForm.lag_days}
                                            onChange={(e) => setCalcForm(prev => ({ ...prev, lag_days: e.target.value }))}
                                            inputProps={{ min: 0 }}
                                            sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4} md={2}>
                                        {canAdd && (
                                            <Button
                                                fullWidth
                                                variant="contained"
                                                startIcon={calculating ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                                                onClick={handleCalculateCorrelation}
                                                disabled={calculating}
                                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none', bgcolor: ACCENT, '&:hover': { bgcolor: '#d97706' } }}
                                            >
                                                Calculate
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>

                                {/* Calculation Result */}
                                {calcResult && (
                                    <Box sx={{ mt: 3, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '0.9rem', mb: 1.5 }}>
                                            Calculation Result
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6} sm={3}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', mb: 0.25 }}>
                                                    Correlation Coefficient
                                                </Typography>
                                                <Typography sx={{
                                                    fontFamily,
                                                    fontWeight: 800,
                                                    fontSize: '1.2rem',
                                                    color: getCorrelationColor(calcResult.correlation_coefficient),
                                                }}>
                                                    {formatCorrelation(calcResult.correlation_coefficient)}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', mb: 0.25 }}>
                                                    Leading Indicator
                                                </Typography>
                                                <Chip
                                                    label={calcResult.is_leading_indicator ? 'Yes' : 'No'}
                                                    size="small"
                                                    color={calcResult.is_leading_indicator ? 'success' : 'default'}
                                                    sx={{ fontFamily, fontWeight: 600, mt: 0.5 }}
                                                />
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', mb: 0.25 }}>
                                                    Window
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                                    {calcResult.window_days || '-'} days
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={6} sm={3}>
                                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.75rem', mb: 0.25 }}>
                                                    Lag
                                                </Typography>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                                    {calcResult.lag_days != null ? calcResult.lag_days : '-'} days
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>

                        {/* Correlations List */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AssessmentIcon sx={{ color: '#3b82f6', fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            Correlations History
                                        </Typography>
                                        <Chip label={corrTotalCount} size="small" sx={{ fontFamily, fontWeight: 600, height: 22 }} />
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <TextField
                                            size="small"
                                            placeholder="Filter by symbol..."
                                            value={corrAssetFilter}
                                            onChange={(e) => { setCorrAssetFilter(e.target.value); setCorrPage(0); }}
                                            sx={{ width: 200, '& .MuiInputBase-root': { fontFamily, fontSize: '0.85rem' } }}
                                        />
                                        <Tooltip title="Refresh">
                                            <IconButton size="small" onClick={fetchCorrelations}>
                                                <RefreshIcon />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>

                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Asset A</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Asset B</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Coefficient</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Window</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Lag</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Leading</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Regime</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Date</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {corrLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                        <CircularProgress size={28} />
                                                    </TableCell>
                                                </TableRow>
                                            ) : correlations.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                                                        <AssessmentIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                            No correlations found
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                correlations.map((corr, index) => (
                                                    <TableRow key={corr.id || index} hover>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                {corr.asset_a_type && (
                                                                    <Chip
                                                                        label={CorrelationAssetTypeLabels[corr.asset_a_type] || corr.asset_a_type}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ fontFamily, fontSize: '0.65rem', height: 20 }}
                                                                    />
                                                                )}
                                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                                    {corr.asset_a_symbol || '-'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                                {corr.asset_b_type && (
                                                                    <Chip
                                                                        label={CorrelationAssetTypeLabels[corr.asset_b_type] || corr.asset_b_type}
                                                                        size="small"
                                                                        variant="outlined"
                                                                        sx={{ fontFamily, fontSize: '0.65rem', height: 20 }}
                                                                    />
                                                                )}
                                                                <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem' }}>
                                                                    {corr.asset_b_symbol || '-'}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{
                                                                fontFamily,
                                                                fontWeight: 700,
                                                                fontSize: '0.85rem',
                                                                color: getCorrelationColor(corr.correlation_coefficient),
                                                            }}>
                                                                {formatCorrelation(corr.correlation_coefficient)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {corr.window_days || '-'}d
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {corr.lag_days != null ? corr.lag_days : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={corr.is_leading_indicator ? 'Yes' : 'No'}
                                                                size="small"
                                                                color={corr.is_leading_indicator ? 'success' : 'default'}
                                                                sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            {corr.macro_regime ? (
                                                                <Chip
                                                                    label={MacroRegimeLabels[corr.macro_regime] || corr.macro_regime}
                                                                    size="small"
                                                                    color={MacroRegimeColors[corr.macro_regime] || 'default'}
                                                                    sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', height: 24 }}
                                                                />
                                                            ) : '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                                {corr.calculated_at
                                                                    ? new Date(corr.calculated_at).toLocaleDateString()
                                                                    : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={corrTotalCount}
                                    page={corrPage}
                                    onPageChange={(e, newPage) => setCorrPage(newPage)}
                                    rowsPerPage={corrRowsPerPage}
                                    onRowsPerPageChange={(e) => { setCorrRowsPerPage(parseInt(e.target.value, 10)); setCorrPage(0); }}
                                    rowsPerPageOptions={[5, 10, 25]}
                                    sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                )}

                {/* ==================== TAB 2: BTC Price History ==================== */}
                {activeTab === 2 && (
                    <Box>
                        {/* Date Filters */}
                        <Paper sx={{ p: 2.5, mb: 3, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={6} sm={3} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Start Date"
                                        type="date"
                                        value={priceStartDate}
                                        onChange={(e) => { setPriceStartDate(e.target.value); setPricePage(0); }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="End Date"
                                        type="date"
                                        value={priceEndDate}
                                        onChange={(e) => { setPriceEndDate(e.target.value); setPricePage(0); }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiInputBase-root': { fontFamily } }}
                                    />
                                </Grid>
                                <Grid item>
                                    <Tooltip title="Refresh">
                                        <IconButton onClick={fetchPriceData}>
                                            <RefreshIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Price Data Table */}
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 0 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2.5, pt: 2.5, pb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CurrencyBitcoinIcon sx={{ color: ACCENT, fontSize: 22 }} />
                                        <Typography sx={{ fontFamily, fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
                                            BTC Price History
                                        </Typography>
                                        <Chip label={`${priceTotalCount} records`} size="small" sx={{ fontFamily, fontWeight: 600, height: 22 }} />
                                    </Box>
                                </Box>

                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Price ($)</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Volume</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Market Cap</TableCell>
                                                <TableCell align="right" sx={{ fontFamily, fontWeight: 600 }}>Realized Vol</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Source</TableCell>
                                                <TableCell sx={{ fontFamily, fontWeight: 600 }}>Timestamp</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {priceLoading ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                        <CircularProgress size={28} />
                                                    </TableCell>
                                                </TableRow>
                                            ) : priceData.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                        <CurrencyBitcoinIcon sx={{ fontSize: 40, color: '#e2e8f0', mb: 1 }} />
                                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>
                                                            No BTC price data available
                                                        </Typography>
                                                        <Typography sx={{ fontFamily, color: '#cbd5e1', fontSize: '0.78rem', mt: 0.5 }}>
                                                            Use the "Fetch Price" button on the dashboard to retrieve data
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                priceData.map((row, index) => (
                                                    <TableRow key={row.id || index} hover>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.85rem', color: '#1e293b' }}>
                                                                {row.price != null
                                                                    ? `$${parseFloat(row.price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                                    : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {formatLargeNumber(row.volume)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {formatLargeNumber(row.market_cap)}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b' }}>
                                                                {row.realized_volatility != null
                                                                    ? parseFloat(row.realized_volatility).toFixed(4)
                                                                    : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                                {row.source || '-'}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>
                                                                {row.timestamp
                                                                    ? new Date(row.timestamp).toLocaleString()
                                                                    : '-'}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                                <TablePagination
                                    component="div"
                                    count={priceTotalCount}
                                    page={pricePage}
                                    onPageChange={(e, newPage) => setPricePage(newPage)}
                                    rowsPerPage={priceRowsPerPage}
                                    onRowsPerPageChange={(e) => { setPriceRowsPerPage(parseInt(e.target.value, 10)); setPricePage(0); }}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontFamily } }}
                                />
                            </CardContent>
                        </Card>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default BitcoinCorrelationPage;
